import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { FieldArray } from 'react-final-form-arrays';
import { FieldValidator } from 'final-form';
import { Field, useField, useForm } from 'react-final-form';
import TextAreaField from 'components/TextAreaField';
import { Button } from 'ncoded-component-library';
import XIcon from 'icons/X.icon';
import { useTranslation } from 'react-i18next';
import PluscircleIcon from 'icons/Pluscircle.icon';
import FieldWrapper from 'components/FieldWrapper';
import { FieldWrapperProps } from 'components/FieldWrapper/FieldWrapper.component';

import './TextareaArrayField.styles.scss';

type TextareaArrayFieldProps = FieldWrapperProps & {
  fieldLabel?: string;
  fieldPlaceholder?: string;
  defaultValue?: string[];
  maxFields?: number;
  maxFieldLength?: number;
  minRequired?: number;
  validate?: FieldValidator<any[]>;
  customOnBlur?: (value: string[], isValid: boolean) => void;
};

const TextareaArrayField: React.FC<TextareaArrayFieldProps> = (props) => {
  const {
    className,
    name,
    fieldLabel,
    fieldPlaceholder,
    required,
    defaultValue,
    maxFields = 10,
    maxFieldLength,
    minRequired = required ? 1 : 0,
    validate,
    customOnBlur,
    ...rest
  } = props;

  const classes = classNames(
    'textarea-array-field',
    { 'with-label': fieldLabel },
    className,
  );

  const { t } = useTranslation();
  const { batch, change } = useForm();
  const {
    input: { value },
    meta: { error, touched, dirty },
  } = useField(name);

  const hasError = useMemo(
    () => (touched || dirty) && error,
    [dirty, error, touched],
  );

  useEffect(() => {
    batch(() => {
      change(name, value.length ? value : defaultValue);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FieldWrapper
      className={classes}
      required={required}
      name={name}
      error={error}
      hasError={hasError}
      {...rest}
    >
      <FieldArray name={name} validate={validate}>
        {({ fields }) => (
          <>
            {fields.map((name, index) => (
              <div key={name} className="textarea-array-field__row">
                <Field
                  name={name}
                  component={TextAreaField}
                  rows={1}
                  label={fieldLabel && `${fieldLabel} ${index + 1}`}
                  placeholder={fieldPlaceholder}
                  maxLength={maxFieldLength}
                  customOnBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                    const newValues = [...value];
                    newValues[index] = e.target.value || undefined;

                    customOnBlur?.(newValues, !hasError);
                  }}
                />
                {!!index && minRequired < index + 1 && (
                  <Button
                    variant="icon"
                    aria-label="Remove"
                    icon={XIcon}
                    className={classNames(
                      'textarea-array-field__close-button',
                      {
                        'with-max-length': maxFieldLength,
                      },
                    )}
                    onClick={() => {
                      fields.remove(index);

                      const newFields = [...value];
                      newFields.splice(index, 1);
                      customOnBlur?.(newFields, !hasError);
                    }}
                  />
                )}
              </div>
            ))}

            {fields.length < maxFields && (
              <Button
                onClick={() => {
                  fields.push(undefined);
                }}
                variant="link"
                className="textarea-array-field__add-button"
              >
                <PluscircleIcon />
                {`${t('addAnother')} ${fieldLabel?.toLowerCase()}`}
              </Button>
            )}
          </>
        )}
      </FieldArray>
    </FieldWrapper>
  );
};

export default TextareaArrayField;
